import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CategoryScroller = ({ categories, selectedCategory, onSelectCategory }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScrollPosition();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScrollPosition);
      return () => ref.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // Category icons/emojis mapping
  const categoryIcons = {
    'todos': '🦐',
    'promociones': '🔥',
    'bebidas': '🍹',
    'caldos': '🍲',
    'camarones': '🦐',
    'cocos': '🥥',
    'cocteles': '🍸',
    'filetes': '🐟',
    'tacos': '🌮',
    'tostadas': '🫓',
    'ceviches': '🥣',
    'aguachiles': '🌶️',
    'postres': '🍮',
    'entradas': '🥗',
    'default': '🍽️'
  };

  const getIcon = (categoryName) => {
    const key = categoryName?.toLowerCase() || 'default';
    return categoryIcons[key] || categoryIcons['default'];
  };

  return (
    <div className="relative mb-6" data-testid="category-scroller">
      {/* Gradient fade left */}
      {showLeftArrow && (
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none" />
      )}
      
      {/* Gradient fade right */}
      {showRightArrow && (
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none" />
      )}

      {/* Left arrow */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-slate-800/90 hover:bg-slate-700 text-white p-1.5 rounded-full shadow-lg transition-all"
          data-testid="category-scroll-left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      {/* Right arrow */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-slate-800/90 hover:bg-slate-700 text-white p-1.5 rounded-full shadow-lg transition-all"
          data-testid="category-scroll-right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth px-1 py-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {categories.map((category, index) => {
          const isSelected = selectedCategory === category.name || 
            (category.name === 'Todos' && !selectedCategory);
          
          return (
            <button
              key={category.category_id || index}
              onClick={() => onSelectCategory(category.name === 'Todos' ? null : category.name)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap snap-start
                transition-all duration-300 font-medium text-sm
                ${isSelected 
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30 scale-105' 
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 hover:text-white border border-white/10'
                }
              `}
              data-testid={`category-pill-${category.name?.toLowerCase().replace(/\s+/g, '-') || index}`}
            >
              <span className="text-lg">{getIcon(category.name)}</span>
              <span>{category.name}</span>
              {category.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isSelected ? 'bg-white/20' : 'bg-slate-700'
                }`}>
                  {category.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default CategoryScroller;
